import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../ui/Button';
import { BILLING_PATH } from './accessRoutes';

/**
 * The lock itself: the app behind it, unusable, with the reason on top.
 *
 * Written for the person most likely to hit it — an ordinary member who has no idea why the app
 * stopped working and no power to fix it. So it says what has happened to the ACCOUNT, and who to
 * ask. Earlier copy addressed whoever placed the hold, which is the rarest reader of the three.
 *
 * Not dismissable and not closable — the page underneath is blurred and inert (pointer events off,
 * hidden from assistive tech) so nothing can be read or clicked through it. The server refuses
 * writes independently, so removing this in devtools gets nobody anywhere.
 */
export function AccessSuspendedLock({ note }: { note: string | null }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="access-locked-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-line bg-card p-8 text-center shadow-panel">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-warning-dark-tint">
          <Lock className="h-6 w-6 text-amber-700 dark:text-warning-dark" aria-hidden />
        </span>
        <h1 id="access-locked-title" className="mt-4 text-lg font-semibold text-ink">
          This account is on hold
        </h1>
        <p className="mt-2 text-sm text-ink-label">
          The entire account has been placed on hold, so Smorgas Social is unavailable for everyone
          here until it is resolved. Contact your Billing Admin to review the billing details and
          have the hold lifted.
        </p>
        {/* The vendor's own wording, if they gave any — shown ALONGSIDE the guidance rather than
            instead of it, so a terse internal note never leaves the reader without a next step. */}
        {note && <p className="mt-3 text-sm italic text-ink-muted">“{note}”</p>}
        <div className="mt-6 flex flex-col gap-2">
          {/* Billing for everyone: it is readable by every member, it is where the hold is
              explained in full, and from there the rest of the app is navigable for the people who
              can actually act on it. */}
          <Button onClick={() => navigate(BILLING_PATH)}>
            <CreditCard className="h-4 w-4" aria-hidden />
            Go to Billing
          </Button>
          {/* The chrome behind this is inert, so without this there is no way out of the app. */}
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
        <p className="mt-4 text-xs text-ink-faint">
          Nothing has been deleted. Everything comes straight back when the hold is lifted.
        </p>
      </div>
    </div>
  );
}
